/**
 * DispatchNotificationSettings
 *
 * Settings page for dispatcher notification preferences (Issue 6.3)
 * Allows dispatchers to configure what alerts they receive and through which channels.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControlLabel,
  Switch,
  TextField,
  Button,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  IconButton,
  Chip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SendIcon from '@mui/icons-material/Send';
import SaveIcon from '@mui/icons-material/Save';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import {
  fetchNotificationSettings,
  updateNotificationSettings,
  sendTestNotification,
} from '../../../../../api/dispatch/dispatchApi';
import {
  NotificationSettings,
  UpdateNotificationSettingsRequest,
  NotificationChannel,
} from '../../../../../api/dispatch/types';
import { usePushNotifications } from '../../hooks/dispatch';
import styles from './DispatchNotificationSettings.module.css';

// Alert type configuration interface
interface AlertTypeConfig {
  key: keyof NotificationSettings;
  label: string;
  description: string;
  alwaysEnabled?: boolean;
  hasThreshold?: boolean;
  thresholdKey?: keyof NotificationSettings;
  thresholdLabel?: string;
  thresholdMin?: number;
  thresholdMax?: number;
}

// Channel configuration interface
interface ChannelConfig {
  key: keyof NotificationSettings;
  label: string;
  icon: string;
  requiresSetup?: boolean;
}

// Alert type configuration with labels and descriptions
const ALERT_TYPES: AlertTypeConfig[] = [
  {
    key: 'notifyEmergency',
    label: 'Emergency SOS',
    description: 'Guard pressed the emergency button',
    alwaysEnabled: true,
  },
  {
    key: 'notifyNoActivity',
    label: 'No Activity',
    description: 'Guard has no location updates during patrol',
    hasThreshold: true,
    thresholdKey: 'noActivityMinutes',
    thresholdLabel: 'minutes',
    thresholdMin: 5,
    thresholdMax: 120,
  },
  {
    key: 'notifyLeftSite',
    label: 'Guard Left Site',
    description: 'Guard left the geofenced area during patrol',
  },
  {
    key: 'notifyLowBattery',
    label: 'Low Battery',
    description: 'Guard\'s device battery is low',
    hasThreshold: true,
    thresholdKey: 'lowBatteryPercent',
    thresholdLabel: '%',
    thresholdMin: 5,
    thresholdMax: 50,
  },
  {
    key: 'notifyIncompletePatrol',
    label: 'Incomplete Patrol',
    description: 'Patrol running too long or guard stopped responding',
  },
  {
    key: 'notifyPatrolStart',
    label: 'Patrol Started',
    description: 'Guard started a patrol (informational)',
  },
  {
    key: 'notifyPatrolComplete',
    label: 'Patrol Completed',
    description: 'Guard completed a patrol (informational)',
  },
  {
    key: 'notifyVmsIncident',
    label: 'Camera Incident',
    description: 'VMS camera detected an incident',
  },
];

// Channel configuration
const CHANNELS: ChannelConfig[] = [
  { key: 'emailEnabled', label: 'Email', icon: '📧' },
  { key: 'slackEnabled', label: 'Slack', icon: '💬', requiresSetup: true },
  { key: 'teamsEnabled', label: 'Microsoft Teams', icon: '👥', requiresSetup: true },
  { key: 'browserPushEnabled', label: 'Browser Push', icon: '🔔' },
];

const DispatchNotificationSettings: React.FC = () => {
  // State
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [testChannel, setTestChannel] = useState<NotificationChannel>('email');
  const [isTesting, setIsTesting] = useState(false);

  // Push notifications hook (Issue 6.4)
  const {
    isSupported: pushSupported,
    isServerConfigured: pushServerConfigured,
    permission: pushPermission,
    isSubscribed: pushSubscribed,
    isLoading: pushLoading,
    error: pushError,
    subscribe: subscribePush,
    unsubscribe: unsubscribePush,
  } = usePushNotifications();

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchNotificationSettings();
      setSettings(data);
    } catch (err) {
      setError('Failed to load notification settings. Please try again.');
      console.error('[DispatchNotificationSettings/loadSettings] Failed to load settings:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle setting change
  const handleSettingChange = useCallback(
    (key: keyof UpdateNotificationSettingsRequest, value: boolean | number | string | null) => {
      if (!settings) return;

      setSettings((prev) => {
        if (!prev) return prev;
        return { ...prev, [key]: value };
      });
      setHasChanges(true);
    },
    [settings]
  );

  // Save settings
  const handleSave = async () => {
    if (!settings) return;

    setIsSaving(true);
    setError(null);
    try {
      const updated = await updateNotificationSettings({
        notifyNoActivity: settings.notifyNoActivity,
        notifyLeftSite: settings.notifyLeftSite,
        notifyLowBattery: settings.notifyLowBattery,
        notifyIncompletePatrol: settings.notifyIncompletePatrol,
        notifyPatrolStart: settings.notifyPatrolStart,
        notifyPatrolComplete: settings.notifyPatrolComplete,
        notifyVmsIncident: settings.notifyVmsIncident,
        notifyVmsCriticalOnly: settings.notifyVmsCriticalOnly,
        noActivityMinutes: settings.noActivityMinutes,
        lowBatteryPercent: settings.lowBatteryPercent,
        emailEnabled: settings.emailEnabled,
        slackEnabled: settings.slackEnabled,
        teamsEnabled: settings.teamsEnabled,
        browserPushEnabled: settings.browserPushEnabled,
        quietHoursEnabled: settings.quietHoursEnabled,
        quietHoursStart: settings.quietHoursStart,
        quietHoursEnd: settings.quietHoursEnd,
      });
      setSettings(updated);
      setHasChanges(false);
      setSuccessMessage('Settings saved successfully');
    } catch (err) {
      setError('Failed to save settings. Please try again.');
      console.error('[DispatchNotificationSettings/handleSave] Failed to save settings:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Send test notification
  const handleTestNotification = async () => {
    setIsTesting(true);
    setError(null);
    try {
      const result = await sendTestNotification(testChannel);
      setSuccessMessage(result.message);
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error && 'response' in err
          ? (err as { response?: { data?: { error?: { message?: string } } } }).response?.data
              ?.error?.message
          : 'Failed to send test notification';
      setError(errorMessage || 'Failed to send test notification');
      console.error('[DispatchNotificationSettings/handleTestNotification] Failed to send test notification:', err);
    } finally {
      setIsTesting(false);
    }
  };

  // Render loading state
  if (isLoading) {
    return (
      <Box className={styles.loadingContainer}>
        <CircularProgress />
        <Typography>Loading notification settings...</Typography>
      </Box>
    );
  }

  if (!settings) {
    return (
      <Box className={styles.errorContainer}>
        <Alert severity="error">{error || 'Failed to load settings'}</Alert>
        <Button variant="contained" onClick={loadSettings} sx={{ mt: 2 }}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Paper className={styles.paper}>
        {/* Header */}
        <Box className={styles.header}>
          <Typography variant="h5" className={styles.title}>
            Notification Settings
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Configure which alerts you receive and how they're delivered
          </Typography>
        </Box>

        <Divider />

        {/* Alert Types Section */}
        <Box className={styles.section}>
          <Typography variant="h6" className={styles.sectionTitle}>
            Alert Types
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Choose which events trigger notifications
          </Typography>

          <Box className={styles.alertTypesGrid}>
            {ALERT_TYPES.map((alertType) => {
              const settingKey = alertType.key as keyof NotificationSettings;
              const isEnabled = alertType.alwaysEnabled || settings[settingKey];
              const thresholdKey = alertType.hasThreshold
                ? (alertType.thresholdKey as keyof NotificationSettings)
                : null;
              const thresholdValue = thresholdKey ? settings[thresholdKey] : null;

              return (
                <Box key={alertType.key} className={styles.alertTypeRow}>
                  <Box className={styles.alertTypeInfo}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(isEnabled)}
                          onChange={(e) =>
                            handleSettingChange(
                              alertType.key as keyof UpdateNotificationSettingsRequest,
                              e.target.checked
                            )
                          }
                          disabled={alertType.alwaysEnabled}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1">
                            {alertType.label}
                            {alertType.alwaysEnabled && (
                              <Typography
                                component="span"
                                variant="caption"
                                color="textSecondary"
                                sx={{ ml: 1 }}
                              >
                                (Always enabled)
                              </Typography>
                            )}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {alertType.description}
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>

                  {alertType.hasThreshold && thresholdKey && (
                    <Box className={styles.thresholdInput}>
                      <TextField
                        type="number"
                        size="small"
                        value={thresholdValue ?? ''}
                        onChange={(e) =>
                          handleSettingChange(
                            thresholdKey as keyof UpdateNotificationSettingsRequest,
                            parseInt(e.target.value, 10) || alertType.thresholdMin || 5
                          )
                        }
                        inputProps={{
                          min: alertType.thresholdMin,
                          max: alertType.thresholdMax,
                        }}
                        disabled={!isEnabled}
                        sx={{ width: 80 }}
                      />
                      <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
                        {alertType.thresholdLabel}
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>

        <Divider />

        {/* Notification Channels Section */}
        <Box className={styles.section}>
          <Typography variant="h6" className={styles.sectionTitle}>
            Notification Channels
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Choose how you want to receive notifications
          </Typography>

          <Box className={styles.channelsGrid}>
            {CHANNELS.map((channel) => {
              const settingKey = channel.key as keyof NotificationSettings;
              const isEnabled = settings[settingKey];

              // Special handling for browser push (Issue 6.4)
              if (channel.key === 'browserPushEnabled') {
                return (
                  <Box key={channel.key} className={styles.channelRow}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(isEnabled) && pushSubscribed}
                            onChange={async (e) => {
                              if (e.target.checked) {
                                // Enable: request permission and subscribe
                                const success = await subscribePush();
                                if (success) {
                                  handleSettingChange('browserPushEnabled', true);
                                  setSuccessMessage('Browser push notifications enabled');
                                } else if (pushError) {
                                  setError(pushError);
                                }
                              } else {
                                // Disable: unsubscribe
                                await unsubscribePush();
                                handleSettingChange('browserPushEnabled', false);
                                setSuccessMessage('Browser push notifications disabled');
                              }
                            }}
                            disabled={!pushSupported || !pushServerConfigured || pushLoading}
                          />
                        }
                        label={
                          <Box className={styles.channelLabel}>
                            <span className={styles.channelIcon}>{channel.icon}</span>
                            <Typography variant="body1">{channel.label}</Typography>
                          </Box>
                        }
                      />

                      {/* Push status indicator */}
                      {pushLoading ? (
                        <CircularProgress size={20} />
                      ) : !pushSupported ? (
                        <Chip
                          icon={<ErrorIcon />}
                          label="Not supported"
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                      ) : !pushServerConfigured ? (
                        <Chip
                          icon={<ErrorIcon />}
                          label="Server not configured"
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      ) : pushPermission === 'denied' ? (
                        <Chip
                          icon={<NotificationsOffIcon />}
                          label="Permission denied"
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                      ) : pushSubscribed ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Active"
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ) : pushPermission === 'granted' ? (
                        <Chip
                          icon={<NotificationsActiveIcon />}
                          label="Permission granted"
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      ) : null}
                    </Box>

                    {/* Help text for push notifications */}
                    {!pushSupported && (
                      <Typography variant="caption" color="error" sx={{ ml: 4, display: 'block' }}>
                        Your browser doesn't support push notifications
                      </Typography>
                    )}
                    {pushSupported && !pushServerConfigured && (
                      <Typography variant="caption" color="warning.main" sx={{ ml: 4, display: 'block' }}>
                        Push notifications are not configured on the server. Contact your administrator.
                      </Typography>
                    )}
                    {pushPermission === 'denied' && (
                      <Typography variant="caption" color="error" sx={{ ml: 4, display: 'block' }}>
                        You previously denied notification permission. Please enable notifications in your browser settings.
                      </Typography>
                    )}
                  </Box>
                );
              }

              // Standard channel rendering
              return (
                <Box key={channel.key} className={styles.channelRow}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(isEnabled)}
                        onChange={(e) =>
                          handleSettingChange(
                            channel.key as keyof UpdateNotificationSettingsRequest,
                            e.target.checked
                          )
                        }
                      />
                    }
                    label={
                      <Box className={styles.channelLabel}>
                        <span className={styles.channelIcon}>{channel.icon}</span>
                        <Typography variant="body1">{channel.label}</Typography>
                        {channel.requiresSetup && (
                          <Tooltip title="Requires webhook configuration in CANsense settings">
                            <IconButton size="small">
                              <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    }
                  />
                </Box>
              );
            })}
          </Box>
        </Box>

        <Divider />

        {/* Quiet Hours Section */}
        <Box className={styles.section}>
          <Typography variant="h6" className={styles.sectionTitle}>
            Quiet Hours
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Suppress non-emergency notifications during specified hours
          </Typography>

          <Box className={styles.quietHoursRow}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.quietHoursEnabled}
                  onChange={(e) => handleSettingChange('quietHoursEnabled', e.target.checked)}
                />
              }
              label="Enable quiet hours"
            />

            {settings.quietHoursEnabled && (
              <Box className={styles.quietHoursInputs}>
                <TextField
                  label="From"
                  type="time"
                  size="small"
                  value={settings.quietHoursStart || '22:00'}
                  onChange={(e) => handleSettingChange('quietHoursStart', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 130 }}
                />
                <Typography variant="body2" sx={{ mx: 1 }}>
                  to
                </Typography>
                <TextField
                  label="To"
                  type="time"
                  size="small"
                  value={settings.quietHoursEnd || '06:00'}
                  onChange={(e) => handleSettingChange('quietHoursEnd', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 130 }}
                />
              </Box>
            )}
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            Emergency SOS notifications are never silenced, even during quiet hours.
          </Alert>
        </Box>

        <Divider />

        {/* Actions Section */}
        <Box className={styles.actionsSection}>
          {/* Test Notification */}
          <Box className={styles.testNotification}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Test Channel</InputLabel>
              <Select
                value={testChannel}
                label="Test Channel"
                onChange={(e) => setTestChannel(e.target.value as NotificationChannel)}
              >
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="slack">Slack</MenuItem>
                <MenuItem value="teams">Microsoft Teams</MenuItem>
                <MenuItem value="browser_push">Browser Push</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              onClick={handleTestNotification}
              disabled={isTesting}
              startIcon={isTesting ? <CircularProgress size={16} /> : <SendIcon />}
            >
              {isTesting ? 'Sending...' : 'Send Test'}
            </Button>
          </Box>

          {/* Save Button */}
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
            startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
            size="large"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Paper>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DispatchNotificationSettings;
